import React, { useContext } from "react";
import { MenuComponentProps } from "../types";
import { FileSystem, Screen, Menu } from "../";
import { saveAs } from "file-saver";
import { Auth } from "../../../contexts/ridersUserContext";
import { FormattedMessage } from "react-intl";
import { Communication } from "../../../contexts/communicationContext";
import { useDocumentationFilePath } from "../../../contexts/documentationFilePathContext";
import {
  DownloadIcon,
  PublishIcon,
  UploadIcon,
} from "../../shared/icons/icons";

const MainMenu: React.FC<MenuComponentProps> = ({
  clickAndCloseHandler,
  showModal,
}) => {
  const { project, refreshFunction, activeChapter, blocklySource }: any =
    React.useContext(Screen.context);

  const { readFile, writeFile }: any = React.useContext(FileSystem.context);

  const { setProjectType, userCodeFilePath }: any = React.useContext(
    Auth.context
  );

  const { documentationFilePath, hasEditor } = useDocumentationFilePath();

  var fileSelector: HTMLInputElement | undefined = undefined;

  const handleUpload = (
    onFileLoaded: (e: ProgressEvent<FileReader>) => void
  ) => {
    if (typeof fileSelector === "undefined") {
      fileSelector = document.createElement("input");
      fileSelector.type = "file";
      fileSelector.style.display = "none";
      document.body.appendChild(fileSelector);
    }
    fileSelector.onchange = (e) => {
      if (!fileSelector?.files || fileSelector?.files?.length !== 1) return;

      let reader = new FileReader();
      reader.readAsText(fileSelector?.files[0], "UTF-8");

      reader.onload = onFileLoaded;

      reader.onerror = (e) => {
        // message.error("File could not imported", 2);
      };

      document.body.removeChild(fileSelector);
    };

    fileSelector.click();
  };

  const { filePath } = useContext<any>(Communication.context);

  const handleDownload = async (file_path: string, save_name: string) => {
    const code = await readFile(file_path);
    const blob = new Blob([code], {
      type: "text/plain;charset=utf-8",
    });
    saveAs(blob, save_name);
  };

  const hasCode = activeChapter?.cells.some((cell: any) => {
    return (
      cell.type === "Code" ||
      (project.documentationUrl ===
        "https://d32lhafmwoak77.cloudfront.net/media/pythonCourse/chapter12/index.html" &&
        cell.type === "Documentation" &&
        hasEditor)
    );
  });
  const hasIno = userCodeFilePath.includes(".ino");

  const hasSpecialDocumentation = activeChapter?.cells.some(
    (cell: any) =>
      project.documentationUrl ===
        "https://d32lhafmwoak77.cloudfront.net/media/pythonCourse/chapter12/index.html" &&
      cell.type === "Documentation" &&
      hasEditor
  );

  return (
    <Menu>
      {[
        {
          title: <FormattedMessage id="userCodeDownloadAction" />,
          icon: <DownloadIcon />,
          disabled: !hasCode,
          onClick: clickAndCloseHandler!(() => {
            const currentFile = filePath.split("/").pop();

            if (hasSpecialDocumentation) {
              let filePath: string;
              let fileName: string;

              // Use file path from documentation iframe if available
              if (documentationFilePath) {
                filePath = documentationFilePath;
                fileName =
                  documentationFilePath.split("/").pop() || "user_code.py";
              } else {
                // Fallback to deriving from chapter data
                const documentationCell = activeChapter?.cells.find(
                  (cell: any) => cell.type === "Documentation"
                );

                // Try to derive source code from various sources
                let sourceCode = documentationCell?.sourceCode;

                if (!sourceCode) {
                  // Try to use English chapter name for file paths
                  const englishName =
                    activeChapter?.nameEn ||
                    activeChapter?.nameTr ||
                    activeChapter?.name;
                  if (englishName && englishName !== "index") {
                    // Remove spaces and special characters from English chapter name
                    const cleanChapterName = englishName
                      .replace(/\s+/g, "")
                      .replace(/[^a-zA-Z0-9]/g, "");
                    sourceCode = `user_code_${cleanChapterName}`;
                  } else if (
                    documentationCell?.relativePath &&
                    !documentationCell.relativePath.includes("index.html")
                  ) {
                    // Extract identifier from relativePath if it's not index.html
                    const pathMatch =
                      documentationCell.relativePath.match(/([^\/]+)\.html/);
                    if (pathMatch && pathMatch[1] !== "index") {
                      sourceCode = `user_code_${pathMatch[1]}`;
                    }
                  }
                }

                sourceCode = sourceCode || "user_code";
                filePath = `/usercode/${sourceCode}.py`;
                fileName = `${sourceCode}.py`;
              }

              handleDownload(filePath, fileName);
              return;
            }

            const activeChapterCodeSource = activeChapter?.cells.find(
              (cell: any) => cell.type === "Code"
            )?.sourceCode;

            handleDownload(
              `/${
                project.dev === "blockly"
                  ? blocklySource
                  : hasIno
                  ? `usercode/${activeChapterCodeSource}.ino`
                  : `usercode/${currentFile}`
              }`,
              `${
                project.dev === "blockly"
                  ? blocklySource
                  : hasIno
                  ? `${activeChapterCodeSource}.ino`
                  : currentFile
              }`
            );
          }),
        },
        {
          title: <FormattedMessage id="userCodeUploadAction" />,
          icon: <UploadIcon />,
          disabled: !hasCode,
          onClick: clickAndCloseHandler!(() => {
            handleUpload(async (e) => {
              const data = e?.target?.result;

              if (hasSpecialDocumentation) {
                let filePath: string;

                // Use file path from documentation iframe if available
                if (documentationFilePath) {
                  filePath = documentationFilePath;
                } else {
                  // Fallback to deriving from chapter data
                  const documentationCell = activeChapter?.cells.find(
                    (cell: any) => cell.type === "Documentation"
                  );

                  // Try to derive source code from various sources
                  let sourceCode = documentationCell?.sourceCode;

                  if (!sourceCode) {
                    // Try to use English chapter name for file paths
                    const englishName =
                      activeChapter?.nameEn ||
                      activeChapter?.nameTr ||
                      activeChapter?.name;
                    if (englishName && englishName !== "index") {
                      // Remove spaces and special characters from English chapter name
                      const cleanChapterName = englishName
                        .replace(/\s+/g, "")
                        .replace(/[^a-zA-Z0-9]/g, "");
                      sourceCode = `user_code_${cleanChapterName}`;
                    } else if (
                      documentationCell?.relativePath &&
                      !documentationCell.relativePath.includes("index.html")
                    ) {
                      // Extract identifier from relativePath if it's not index.html
                      const pathMatch =
                        documentationCell.relativePath.match(/([^\/]+)\.html/);
                      if (pathMatch && pathMatch[1] !== "index") {
                        sourceCode = `user_code_${pathMatch[1]}`;
                      }
                    }
                  }

                  sourceCode = sourceCode || "user_code";
                  filePath = `/usercode/${sourceCode}.py`;
                }

                writeFile(filePath, data);
                setTimeout(() => {
                  refreshFunction();
                }, 1000);
                return;
              }

              const activeChapterCodeSource = activeChapter?.cells.find(
                (cell: any) => cell.type === "Code"
              )?.sourceCode;

              writeFile(
                `${
                  project.dev === "blockly"
                    ? `/${blocklySource}`
                    : hasIno
                    ? `/usercode/${activeChapterCodeSource}.ino`
                    : `/usercode/${activeChapterCodeSource}.py`
                }`,
                data
              );
              //settimeout
              setTimeout(() => {
                refreshFunction();
              }, 1000);
            });
          }),
        },
        // Only show publish button when hasSpecialDocumentation is false
        ...(!hasSpecialDocumentation
          ? [
              {
                title: <FormattedMessage id="publish" />,
                icon: <PublishIcon />,
                disabled: !hasCode,
                onClick: clickAndCloseHandler!(() => {
                  showModal();
                }),
              },
            ]
          : []),
      ]}
    </Menu>
  );
};

export default MainMenu;
